import { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../lib/api';
import { Plus, Trash2, FileText, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { ExportMenu } from '../components/ExportMenu';
import { ImportDialog } from '../components/ImportDialog';
import { CategoryBadge, CategoryFilter, CategoryPicker } from '../components/CategoryControls';
import { useAuth } from '../contexts/AuthContext';
import { apiErrorMessage, lockedMasterCategory } from '../utils/requestTheme';


export default function RequestTypes() {
  const { user } = useAuth();
  const locked = lockedMasterCategory(user);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  // Dialog tambah/edit: null = tertutup; {id?, name, category}
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const category = locked || filter;

  const fetchTypes = useCallback(async () => {
    try {
      const res = await api.get(`/internal/request-types/all`, { params: category ? { category } : {} });
      setTypes(res.data);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Gagal memuat data'));
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Nama wajib diisi');
    const cat = locked || form.category;
    if (!cat) return toast.error('Pilih kategori IPR atau OPR');
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/internal/request-types/${form.id}`, { name: form.name, category: cat });
        toast.success('Tipe request diperbarui');
      } else {
        await api.post(`/internal/request-types`, { name: form.name, category: cat });
        toast.success('Tipe request berhasil ditambahkan');
      }
      setForm(null);
      fetchTypes();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Gagal menyimpan'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Hapus tipe "${name}"?`)) return;
    try {
      await api.delete(`/internal/request-types/${id}`);
      toast.success('Tipe request dihapus');
      fetchTypes();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Gagal menghapus'));
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="request-types-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Tipe Request</h1>
            <p className="text-slate-500 mt-1">Kategori IPR muncul di form Internal, kategori OPR muncul di form Operasional</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <ExportMenu table="request-types" size="default" params={{ category }} />
            <ImportDialog type="request-types" title="Tipe Request" size="default" onDone={fetchTypes} />
            <Button className="bg-red-600 hover:bg-red-700" onClick={() => setForm({ name: '', category: locked || filter || '' })} data-testid="add-request-type">
              <Plus className="h-4 w-4 mr-2" /> Tambah Tipe
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
            <CardTitle>Daftar Tipe Request ({types.length})</CardTitle>
            <CategoryFilter value={filter} onChange={setFilter} locked={locked} />
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {types.map((t, idx) => (
                <div key={t.id} className="flex items-center justify-between gap-2 p-3 bg-slate-50 rounded-lg border hover:bg-slate-100" data-testid={`request-type-${t.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-sm text-slate-400 w-8">{idx + 1}.</span>
                    <FileText className="h-4 w-4 text-red-500 shrink-0" />
                    <span className="font-medium truncate">{t.name}</span>
                    <CategoryBadge value={t.category} testId={`type-category-${t.id}`} />
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => setForm({ id: t.id, name: t.name, category: t.category })} data-testid={`edit-type-${t.id}`}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(t.id, t.name)} data-testid={`delete-type-${t.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
              {types.length === 0 && (
                <p className="text-center py-8 text-slate-500">Belum ada tipe request</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{form?.id ? 'Edit Tipe Request' : 'Tambah Tipe Request'}</DialogTitle>
            </DialogHeader>
            {form && (
              <form onSubmit={handleSave} className="space-y-4">
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value.toUpperCase() })}
                  placeholder="Nama tipe request (contoh: PERJALANAN DINAS)"
                  className="uppercase"
                  required
                  data-testid="new-type-name"
                />
                <CategoryPicker value={form.category} onChange={(c) => setForm({ ...form, category: c })} locked={locked} testId="type-category" />
                {form.id && <p className="text-xs text-slate-500">Request yang sudah dibuat tetap menyimpan nama tipe lama.</p>}
                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={saving} data-testid="submit-type">
                  {saving ? 'Menyimpan...' : form.id ? 'Simpan' : 'Tambah'}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
